"use client";

import { useProfile } from "@/context/useProfileProps";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";

import NoData from "@/components/profile-performance/noData";

import { NotificationCategory } from "../../types";
import CategoryFilters from "./CategoryFilters";
import NotificationCard from "./NotificationCard";

type NotificationType = {
  notificationId: string;
  notificationType: string;
  timestamp: number;
  company: {
    name: string;
    logoUrl: string;
  };
  otherActions?: {
    count: number;
    list: Array<{
      notificationType: string;
      timestamp: number;
    }>;
  };
};

type RecruiterActionsContentProps = {
  data: {
    success: boolean;
    start: number;
    limit: number;
    total: number;
    notifications: NotificationType[];
    categories: Record<
      NotificationCategory,
      { count: number; notificationTypes?: string[] } | undefined
    >;
  };
  selectedCategory: NotificationCategory;
  onCategoryChange: (category: NotificationCategory) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
};

const DATE_GROUP_ORDER = ["Today", "Yesterday", "Last 7 days", "Last 15 days"];

// Helper to get date group label (Today, Yesterday, Last 7 days, Last 15 days)
const getDateGroupLabel = (timestamp: number): string => {
  const now = new Date();
  const date = new Date(timestamp);

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const diffTime = todayStart.getTime() - dateStart.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays >= 2 && diffDays <= 7) {
    return "Last 7 days";
  }
  return "Last 15 days";
};

// Helper to group notifications by date category
const groupNotificationsByDate = (
  notifications: NotificationType[],
): Map<string, NotificationType[]> => {
  const groups = new Map<string, NotificationType[]>();
  DATE_GROUP_ORDER.forEach((label) => {
    groups.set(label, []);
  });

  const sortedNotifications = [...notifications].sort(
    (a, b) => b.timestamp - a.timestamp,
  );

  sortedNotifications.forEach((notification) => {
    const label = getDateGroupLabel(notification.timestamp);
    const existing = groups.get(label) || [];
    existing.push(notification);
    groups.set(label, existing);
  });

  DATE_GROUP_ORDER.forEach((label) => {
    if (groups.get(label)?.length === 0) {
      groups.delete(label);
    }
  });

  return groups;
};

const RecruiterActionsContent: React.FC<RecruiterActionsContentProps> = ({
  data,
  selectedCategory,
  onCategoryChange,
  onLoadMore,
  hasMore,
  isFetchingMore,
}) => {
  const { isMobile } = useProfile();
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredNotifications = useMemo(() => {
    if (selectedCategory === "ALL") {
      return data.notifications;
    }

    const categoryInfo = data.categories[selectedCategory];
    if (!categoryInfo || !categoryInfo.notificationTypes) {
      return [];
    }

    return data.notifications.filter((notification) =>
      categoryInfo.notificationTypes?.includes(notification.notificationType),
    );
  }, [data.notifications, data.categories, selectedCategory]);

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByDate(filteredNotifications);
  }, [filteredNotifications]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || isFetchingMore) return;
      const { scrollTop, scrollHeight, clientHeight } =
        event.currentTarget as HTMLDivElement;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      if (distanceFromBottom < 120) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, onLoadMore],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0 });
    }
  }, [selectedCategory]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-baseline gap-[6px]">
        <span className="text-lg font-bold leading-[26px] text-[#17142A] md:!text-2xl md:!leading-9">
          {data.categories["ALL"]?.count || data.total}
        </span>
        <span className="text-sm font-bold leading-[20px] text-[#17142A] md:!text-base md:!leading-6">
          Recruiter actions on your profile
        </span>
      </div>

      {data.total && data.total !== 0 ? (
        <>
          <div className="mt-4">
            <CategoryFilters
              categories={data.categories}
              selectedCategory={selectedCategory}
              onCategoryChange={onCategoryChange}
            />
          </div>

          <div
            ref={scrollRef}
            className="no-scrollbar mt-4 flex-1 overflow-y-auto md:!mt-5"
            onScroll={handleScroll}
          >
            {filteredNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-center text-sm text-[#777584]">
                No notifications found for this category
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from(groupedNotifications.entries()).map(
                  ([dateLabel, notifications]) => (
                    <div key={dateLabel} className="flex flex-col gap-[6px]">
                      <div className="text-xs font-normal leading-4 text-[#777584] md:!pl-4">
                        {dateLabel}
                      </div>
                      <div className="flex flex-col">
                        {notifications.map((notification, index) => (
                          <div
                            key={`${notification.notificationId}-${notification.timestamp}-${index}`}
                          >
                            <NotificationCard notification={notification} />
                            {index !== notifications.length - 1 && isMobile && (
                              <div className="my-2 h-[1px] bg-[#E8E8EB]"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )}
                {isFetchingMore && (
                  <div className="py-3 text-center text-xs leading-4 text-[#777584]">
                    Loading more...
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <NoData
          heading="No recruiter actions yet!"
          subHeading="Recruiters haven't discovered your profile yet. To increase your chances of getting noticed keep your profile complete and up-to-date."
          buttonLabel="Explore Jobs"
          buttonUrl="/seeker/dashboard"
        />
      )}
    </div>
  );
};

export default memo(RecruiterActionsContent);
